import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Phone, PhoneCall, PhoneMissed, PhoneOff, Clock,
  MessageCircle, CheckCircle, XCircle, CalendarClock,
  X, Loader, ChevronDown, Edit3, Save, Lock,
  Users, ClipboardList, Send, Trophy, Briefcase, Info, ExternalLink, Mail, MapPin, FileText, FileUp, UserX
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { getWhatsappLink, getActivityMeta, formatActivityMessage } from '../../utils/helpers';
import { useGoogleCalendar } from '../../contexts/GoogleCalendarContext';
import { PREQUALIFICATION_STATUSES, AWARD_STATUSES, PROFILE_STATUSES, LOSS_REASONS, DOCUMENT_STATUSES } from '../../lib/constants';
import { prequalificationService, contactService, citiesService, servicesService, tendersService, API_BASE_URL } from '../../services/api';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker, TimePicker, Toast } from '../ui';
import { SearchableCitySelect } from './ProfileModal';
import PhoneInput from 'react-phone-input-2';
import TenderWonConfirmModal from './TenderWonConfirmModal';
import 'react-phone-input-2/lib/style.css';

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const baseUrl = API_BASE_URL.replace(/\/api$/, '');
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

// ─── Interaction Outcome Definitions ─────────────────────────────────────────
const INTERACTION_CONFIG = {
  Call: [
    { id: 'connected_interested', label: 'Connected - Interested', labelUr: 'رابطہ ہوا - دلچسپی ہے', icon: CheckCircle, color: 'emerald' },
    { id: 'connected_not_interested', label: 'Connected - Not Interested', labelUr: 'رابطہ ہوا - دلچسپی نہیں', icon: XCircle, color: 'red' },
    { id: 'connected_callback', label: 'Connected - Callback Requested', labelUr: 'رابطہ ہوا - دوبارہ کال کریں', icon: PhoneCall, color: 'blue' },
    { id: 'no_answer', label: 'No Answer', labelUr: 'جواب نہیں', icon: PhoneMissed, color: 'amber' },
    { id: 'busy', label: 'Busy / Call Later', labelUr: 'مصروف / بعد میں کال کریں', icon: Clock, color: 'orange' },
    { id: 'wrong_number', label: 'Wrong Number', labelUr: 'غلط نمبر', icon: PhoneOff, color: 'gray' }
  ],
  Meeting: [
    { id: 'conducted_success', label: 'Conducted Successfully', icon: CheckCircle, color: 'emerald' },
    { id: 'rescheduled', label: 'Rescheduled', icon: Clock, color: 'orange' },
    { id: 'client_absent', label: 'Client Absent / No Show', icon: UserX || XCircle, color: 'gray' },
    { id: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'red' }
  ],
  Visit: [
    { id: 'conducted_success', label: 'Conducted Successfully', icon: CheckCircle, color: 'emerald' },
    { id: 'rescheduled', label: 'Rescheduled', icon: Clock, color: 'orange' },
    { id: 'client_absent', label: 'Client Absent / No Show', icon: UserX || XCircle, color: 'gray' },
    { id: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'red' }
  ],
  Email: [
    { id: 'sent', label: 'Email Sent', icon: Send, color: 'blue' },
    { id: 'replied', label: 'Client Replied', icon: MessageCircle, color: 'emerald' },
    { id: 'bounced', label: 'Bounced / Failed', icon: XCircle, color: 'red' }
  ],
  Document: [
    { id: 'submitted', label: 'Submitted / Sent', icon: FileUp, color: 'blue' },
    { id: 'accepted', label: 'Accepted', icon: CheckCircle, color: 'emerald' },
    { id: 'rejected', label: 'Rejected', icon: XCircle, color: 'red' }
  ]
};

const OUTCOME_STYLES = {
  emerald: { btn: 'border-emerald-400 bg-emerald-50 text-emerald-700', icon: 'text-emerald-500' },
  red: { btn: 'border-red-400 bg-red-50 text-red-700', icon: 'text-red-500' },
  blue: { btn: 'border-blue-400 bg-blue-50 text-blue-700', icon: 'text-blue-500' },
  amber: { btn: 'border-amber-400 bg-amber-50 text-amber-700', icon: 'text-amber-500' },
  orange: { btn: 'border-orange-400 bg-orange-50 text-orange-700', icon: 'text-orange-500' },
  gray: { btn: 'border-gray-400 bg-gray-100 text-gray-700', icon: 'text-gray-500' },
};

// ─── Sidebar Tab Definition ───────────────────────────────────────────────────
const TABS = [
  { id: 'interaction', label: 'Interaction', labelUr: 'رابطہ', icon: MessageCircle },
  { id: 'profile', label: 'Profile', labelUr: 'پروفائل', icon: Users },
  { id: 'stage', label: 'Details', labelUr: 'تفصیلات', icon: ClipboardList },
  { id: 'timeline', label: 'Timeline', labelUr: 'ٹائم لائن', icon: Clock },
];

// ─── Stage badge color helper ─────────────────────────────────────────────────
function getStageBadgeClass(stage) {
  if (!stage) return 'bg-slate-100 text-slate-700 border-slate-200';
  const s = stage.toLowerCase();
  if (s === 'won') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s === 'lost' || s === 'disqualified') return 'bg-red-50 text-red-700 border-red-200';
  if (s === 'enlisted') return 'bg-green-50 text-green-700 border-green-200';
  if (s === 'submitted') return 'bg-cyan-50 text-cyan-700 border-cyan-200';
  if (s === 'pending') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (s === 'in process') return 'bg-purple-50 text-purple-700 border-purple-200';
  if (s === 'hold') return 'bg-gray-50 text-gray-700 border-gray-200';
  return 'bg-blue-50 text-blue-700 border-blue-200';
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LogCallModal({ prequalification, currentUser, onClose, onSave, onSaveLead, venues, eventTypes, managers, isProfile = false, onConvertProfile }) {
  const { t, language } = useLanguage();
  const { syncLeadEvent, shouldAutoSync } = useGoogleCalendar();
  const activityEndRef = useRef(null);

  // ── Tab & UI state ─────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('interaction');
  const [toast, setToast] = useState(null);
  const [showConvertPrompt, setShowConvertPrompt] = useState(false);

  // ── Interaction state ──────────────────────────────────────────────────────
  const [interactionType, setInteractionType] = useState('Call');
  const [callLog, setCallLog] = useState({ outcome: '', notes: '', nextCallDate: '', nextCallTime: '', nextActionType: 'Call' });
  const [saving, setSaving] = useState(false);

  // ── Lead form state ────────────────────────────────────────────────────────
  const [formData, setFormData] = useState(() => ({
    ...prequalification,
    stage: isProfile ? (prequalification.status || 'New') : prequalification.stage,
    // Normalize services: API returns objects [{id,name}], dropdown needs strings
    services: (prequalification.services || []).map(s =>
      typeof s === 'object' && s !== null ? s.name : s
    ).filter(Boolean),
  }));
  const isLockedStatus = ['Completed'].includes(formData.status) || ['Completed'].includes(formData.stage);
  const isTender = !isProfile && (prequalification?.type === 'PROPOSAL' || prequalification?.type === 'TENDER' || prequalification?.isTender === true);
  const entityId = (isTender || isProfile) ? (prequalification.originalId || prequalification.id) : prequalification.id;
  const stages = isProfile ? PROFILE_STATUSES : (isTender ? AWARD_STATUSES : PREQUALIFICATION_STATUSES);

  // ── Stage change state ─────────────────────────────────────────────────────
  const [lossReason, setLossReason] = useState(prequalification.lostReason || '');
  const [stageChangeNote, setStageChangeNote] = useState(prequalification.lostReasonNote || '');

  // ── Enlisted / Stage form state ────────────────────────────────────────────
  const [enlistmentData, setEnlistmentData] = useState({
    type: prequalification.enlistmentType || 'TENDER',
    remarks: prequalification.enlistmentRemarks || '',
    documentStatus: prequalification.documentStatus || 'To be Received',
    submissionDate: prequalification.submissionDate ? new Date(prequalification.submissionDate).toISOString().split('T')[0] : '',
    earnestMoneyRequired: prequalification.earnestMoneyRequired || false,
    earnestMoneyValue: prequalification.earnestMoneyValue || '',
    statusRemarks: prequalification.statusRemarks || '',
    attachmentUrl: prequalification.attachmentUrl || '',
  });

  // ── Profile (Contact) state ────────────────────────────────────────────────
  const [contactDraft, setContactDraft] = useState(() => {
    const p = isProfile ? prequalification : prequalification?.profile;
    if (!p) return null;
    return {
      clientName: p.clientName || '',
      designation: p.designation || '',
      contactPersonName: p.contactPersonName || '',
      email: p.email || '',
      mobile: p.mobile || '',
      city: p.city || '',
      contactDetailExt: p.contactDetailExt || '',
      address: p.address || '',
      projectName: p.projectName || '',
    };
  });
  const [contactBaseline, setContactBaseline] = useState(contactDraft ? { ...contactDraft } : null);
  const [contactSaving, setContactSaving] = useState(false);
  const [showWonConfirm, setShowWonConfirm] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);
  const [contactError, setContactError] = useState('');

  const contactDirty = !!contactDraft && !!contactBaseline && (
    contactDraft.clientName !== contactBaseline.clientName ||
    contactDraft.designation !== contactBaseline.designation ||
    contactDraft.contactPersonName !== contactBaseline.contactPersonName ||
    contactDraft.email !== contactBaseline.email ||
    contactDraft.mobile !== contactBaseline.mobile ||
    contactDraft.city !== contactBaseline.city ||
    contactDraft.contactDetailExt !== contactBaseline.contactDetailExt ||
    contactDraft.address !== contactBaseline.address ||
    contactDraft.projectName !== contactBaseline.projectName
  );

  // ── Cities & Services state ────────────────────────────────────────────────
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);

  useEffect(() => {
    let isMounted = true;
    setLoadingCities(true);
    Promise.all([
      citiesService.getAll({ activeOnly: 'true' }).catch(err => {
        console.error('Failed to load cities:', err);
        return [];
      }),
      servicesService.getAll().catch(err => {
        console.error('Failed to load services:', err);
        return [];
      })
    ]).then(([citiesRes, servicesRes]) => {
      if (isMounted) {
        setCities(Array.isArray(citiesRes) ? citiesRes : []);
        setAvailableServices(Array.isArray(servicesRes) ? servicesRes : []);
        setLoadingCities(false);
      }
    });
    return () => { isMounted = false; };
  }, []);

  const handleCreateNewCity = async (cityName) => {
    try {
      const newCity = await citiesService.create({ name: cityName, isActive: true });
      setCities(prev => [...prev, newCity]);
      setContactDraft(prev => ({ ...prev, city: newCity.name }));
      setContactSaved(false);
    } catch (err) {
      throw err;
    }
  };

  // ── Timeline state ─────────────────────────────────────────────────────────
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [commentSending, setCommentSending] = useState(false);

  // ── Dates ──────────────────────────────────────────────────────────────────
  const todayStr = useMemo(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }, []);

  // ── Fetch timeline on mount ────────────────────────────────────────────────
  const fetchTimeline = useCallback(async () => {
    setTimelineLoading(true);
    try {
      const rawActivities = isProfile
        ? await contactService.getProfileTimeline(entityId)
        : (isTender
            ? await tendersService.getTimeline(entityId)
            : await prequalificationService.getTimeline(entityId));
      
      const items = (rawActivities || []).map(a => {
        let content = {};
        let raw = a.content;
        let isProfilePhase = false;
        if (typeof raw === 'string' && raw.startsWith('[Profile Phase] ')) {
          isProfilePhase = true;
          raw = raw.substring('[Profile Phase] '.length);
        }
        try {
          content = JSON.parse(raw);
        } catch {
          content = { note: raw };
        }
        return {
          type: a.type?.toLowerCase() || 'note_added',
          data: content,
          timestamp: new Date(a.createdAt),
          user: a.user?.username || 'Unknown',
          isPrequalHistory: !!a.isPrequalificationHistory,
          isProfileHistory: !!a.isProfileHistory || isProfilePhase
        };
      }).sort((a, b) => a.timestamp - b.timestamp);
      setTimeline(items);
    } catch (err) {
      console.error('Failed to load timeline:', err);
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  }, [entityId, isTender]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  useEffect(() => {
    if (activeTab === 'timeline') {
      setTimeout(() => activityEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [activeTab, timeline]);

  // Auto-calc GP%
  useEffect(() => {
    if (!isTender) return;
    const pv = Number(formData.proposalValue) || 0;
    const gp = Number(formData.grossProfit) || 0;
    if (pv > 0 && gp >= 0) {
      setFormData(prev => ({ ...prev, gpPercent: ((gp / pv) * 100).toFixed(2) }));
    } else {
      setFormData(prev => ({ ...prev, gpPercent: '' }));
    }
  }, [formData.proposalValue, formData.grossProfit, isTender]);

  // ── Add comment ────────────────────────────────────────────────────────────
  const handleAddComment = async () => {
    if (!comment.trim() || commentSending) return;
    setCommentSending(true);
    try {
      const payload = {
        content: JSON.stringify({ note: comment.trim() }),
        type: isTender ? 'NOTE' : 'NOTE_ADDED',
        userId: currentUser?.id,
      };
      if (isProfile) {
        await contactService.addNote(entityId, payload.content);
      } else if (isTender) {
        await tendersService.addNote(entityId, payload);
      } else {
        await prequalificationService.addNote(entityId, payload);
      }
      setComment('');
      await fetchTimeline();
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setCommentSending(false);
    }
  };

  // ── Profile save ───────────────────────────────────────────────────────────
  const handleSaveContact = async () => {
    // For direct Profile records (isProfile=true), the profile IS the prequalification
    const profileId = isProfile ? entityId : prequalification?.profile?.id;
    if (!profileId || !contactDirty) return;
    setContactSaving(true);
    setContactError('');
    const payload = {};
    if (contactDraft.clientName !== contactBaseline.clientName) payload.clientName = contactDraft.clientName.trim();
    if (contactDraft.designation !== contactBaseline.designation) payload.designation = contactDraft.designation.trim();
    if (contactDraft.contactPersonName !== contactBaseline.contactPersonName) payload.contactPersonName = contactDraft.contactPersonName.trim();
    if (contactDraft.email !== contactBaseline.email) payload.email = contactDraft.email.trim();
    if (contactDraft.mobile !== contactBaseline.mobile) payload.mobile = contactDraft.mobile || null;
    if (contactDraft.city !== contactBaseline.city) payload.city = contactDraft.city.trim();
    if (contactDraft.contactDetailExt !== contactBaseline.contactDetailExt) payload.contactDetailExt = contactDraft.contactDetailExt.trim();
    if (contactDraft.address !== contactBaseline.address) payload.address = contactDraft.address.trim();
    if (contactDraft.projectName !== contactBaseline.projectName) payload.projectName = contactDraft.projectName.trim();
    try {
      await contactService.update(profileId, payload);
      
      const changedFields = [];
      const keys = [
        { key: 'clientName', label: 'Client Name' },
        { key: 'designation', label: 'Designation' },
        { key: 'contactPersonName', label: 'Contact Person Name' },
        { key: 'email', label: 'Email' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'city', label: 'City' },
        { key: 'contactDetailExt', label: 'Contact Detail / Ext' },
        { key: 'address', label: 'Address' },
        { key: 'projectName', label: 'Project Name' }
      ];
      keys.forEach(({ key, label }) => {
        if (contactDraft[key] !== contactBaseline[key]) {
          changedFields.push(`${label}: ${contactDraft[key]}`);
        }
      });

      if (changedFields.length > 0) {
        const profileUpdatePayload = {
          content: JSON.stringify({ field: changedFields.join(', ') }),
          type: isTender ? 'details_updated' : 'DETAILS_UPDATED',
          userId: currentUser?.id
        };
        if (isTender) {
          tendersService.addNote(entityId, profileUpdatePayload).catch(e => console.error(e));
        } else {
          prequalificationService.addNote(entityId, profileUpdatePayload).catch(e => console.error(e));
        }
      }

      setContactBaseline({ ...contactDraft });
      setContactSaved(true);
      setToast({ message: 'Profile saved!', type: 'success' });
    } catch (err) {
      setContactError(err?.message || 'Could not save the contact');
    } finally {
      setContactSaving(false);
    }
  };

  // ── Stage change (pills) ───────────────────────────────────────────────────
  const handleStageClick = (s) => {
    if (s === formData.stage || isLockedStatus) return;
    setFormData({ ...formData, stage: s });
    // For profiles: show convert prompt when Interested is selected
    if (isProfile && s === 'Interested') {
      setShowConvertPrompt(true);
    } else {
      setShowConvertPrompt(false);
    }
    // For prequalifications/tenders, navigate to the stage details tab
    if (!isProfile) {
      setActiveTab('stage');
    }
  };

  // ── Call outcome auto next-call time ───────────────────────────────────────
  const calculateNextCall = (outcomeId) => {
    const today = new Date();
    let nextDate = new Date();
    switch (outcomeId) {
      case 'busy':
        nextDate.setHours(today.getHours() + 2);
        if (nextDate.getDate() !== today.getDate()) { nextDate = new Date(today); nextDate.setDate(today.getDate() + 1); nextDate.setHours(10, 0, 0, 0); }
        break;
      case 'no_answer':
      case 'connected_callback':
        nextDate.setDate(today.getDate() + 1);
        if (today.getHours() < 9 || today.getHours() > 18) nextDate.setHours(10, 0, 0, 0);
        break;
      case 'connected_interested':
        nextDate.setDate(today.getDate() + 2);
        nextDate.setHours(11, 0, 0, 0);
        break;
      case 'wrong_number':
      case 'connected_not_interested':
        return { date: '', time: '' };
      default:
        nextDate.setDate(today.getDate() + 1);
        nextDate.setHours(10, 0, 0, 0);
    }
    const dateStr = nextDate.toISOString().split('T')[0];
    const hh = nextDate.getHours().toString().padStart(2, '0');
    const mm = nextDate.getMinutes().toString().padStart(2, '0');
    return { date: dateStr, time: `${hh}:${mm}` };
  };

  const handleOutcomeSelect = (outcomeId) => {
    const { date, time } = calculateNextCall(outcomeId);
    setCallLog({ ...callLog, outcome: outcomeId, nextCallDate: date, nextCallTime: time });
  };

  // ── Main save ──────────────────────────────────────────────────────────────
  const handleSaveCall = async (forceConfirm = false) => {
    const isEnlistedStage = formData.stage === 'Enlisted';
    if (!callLog.outcome && !isEnlistedStage && !isTender) return;

    const isLeadLocked = ['Completed'].includes(prequalification.stage);
    const isLostOutcome = callLog.outcome === 'wrong_number' || callLog.outcome === 'connected_not_interested';
    const friendlyOutcome = INTERACTION_CONFIG[interactionType].find(o => o.id === callLog.outcome)?.label || callLog.outcome;
    const lostReasonMessage = isLostOutcome ? `Lost due to: ${friendlyOutcome}` : '';
    let updatedNotes = callLog.notes;
    if (isLostOutcome && !isLeadLocked) {
      updatedNotes = callLog.notes ? `${callLog.notes}\n\n${lostReasonMessage}` : lostReasonMessage;
    }

    const effectiveStage = (isLostOutcome && !isLeadLocked) ? 'Lost' : formData.stage;

    if (isTender && effectiveStage === 'Won' && forceConfirm !== true) {
      setShowWonConfirm(true);
      return;
    }

    const savePayload = {
      interactionType,
      outcome: callLog.outcome,
      notes: updatedNotes,
      nextCallDate: callLog.nextCallDate || null,
      nextCallTime: callLog.nextCallTime || null,
      nextActionType: callLog.nextActionType || 'Call',
      calledAt: new Date().toISOString(),
      calledBy: currentUser?.name,
      stage: effectiveStage,
      leadNotes: (isLostOutcome && !isLeadLocked)
        ? (prequalification.notes ? `${prequalification.notes}\n\nCall Outcome: ${lostReasonMessage}` : `Call Outcome: ${lostReasonMessage}`)
        : formData.notes,
      lostReason: ((isLostOutcome && !isLeadLocked) || formData.stage === 'Lost') ? (lossReason || null) : null,
      lostReasonNote: ((isLostOutcome && !isLeadLocked) || formData.stage === 'Lost') ? (stageChangeNote || null) : null,
      // Stage-specific fields
      documentStatus: enlistmentData.documentStatus,
      submissionDate: enlistmentData.submissionDate || null,
      earnestMoneyRequired: enlistmentData.earnestMoneyRequired,
      earnestMoneyValue: enlistmentData.earnestMoneyValue ? Number(enlistmentData.earnestMoneyValue) : null,
      statusRemarks: enlistmentData.statusRemarks,
      attachmentUrl: enlistmentData.attachmentUrl,
      enlistmentType: enlistmentData.type,
      enlistmentRemarks: enlistmentData.remarks,
      ...(effectiveStage === 'Won' && prequalification.stage !== 'Won' ? { order_date: new Date().toISOString().split('T')[0] } : {}),
      
      // Tender specific fields
      ...(isTender && {
        title: formData.title,
        type: formData.type,
        assignedTo: formData.assignedTo ? parseInt(formData.assignedTo) : null,
        proposalValue: formData.proposalValue ? Number(formData.proposalValue) : null,
        negotiation: formData.negotiation ? Number(formData.negotiation) : null,
        grossProfit: formData.grossProfit ? Number(formData.grossProfit) : null,
        gpPercent: formData.gpPercent ? Number(formData.gpPercent) : null,
        wht: formData.wht ? Number(formData.wht) : null,
        mobilizationAdvance: formData.mobilizationAdvance ? Number(formData.mobilizationAdvance) : null,
        noOfStaff: formData.noOfStaff ? Number(formData.noOfStaff) : null,
        contractDuration: formData.contractDuration,
        paymentTerms: formData.paymentTerms,
        winningStrategy: formData.winningStrategy,
        notes: formData.notes
      })
    };

    setSaving(true);
    try {
      if (callLog.outcome) {
        await onSave(prequalification.id, savePayload);
      }

      // Log stage/details changes to timeline
      const changedFields = [];
      const formatD = d => d ? new Date(d).toISOString().split('T')[0] : '';
      
      if (enlistmentData.documentStatus !== (prequalification.documentStatus || 'To be Received')) changedFields.push(`Document Status: ${enlistmentData.documentStatus}`);
      if (formatD(enlistmentData.submissionDate) !== formatD(prequalification.submissionDate)) changedFields.push(`Submission Date: ${formatD(enlistmentData.submissionDate)}`);
      if (enlistmentData.earnestMoneyRequired !== (prequalification.earnestMoneyRequired || false)) changedFields.push(`Earnest Money Required: ${enlistmentData.earnestMoneyRequired ? 'Yes' : 'No'}`);
      if (String(enlistmentData.earnestMoneyValue || '') !== String(prequalification.earnestMoneyValue || '')) changedFields.push(`Earnest Money Value: ${enlistmentData.earnestMoneyValue}`);
      if (enlistmentData.statusRemarks !== (prequalification.statusRemarks || '')) changedFields.push(`Status Remarks: ${enlistmentData.statusRemarks}`);
      if (enlistmentData.attachmentUrl !== (prequalification.attachmentUrl || '')) changedFields.push(`Attachment URL: ${enlistmentData.attachmentUrl}`);
      if (enlistmentData.type !== (prequalification.enlistmentType || 'TENDER')) changedFields.push(`Enlistment Type: ${enlistmentData.type}`);
      if (enlistmentData.remarks !== (prequalification.enlistmentRemarks || '')) changedFields.push(`Enlistment Remarks`);

      const oldServices = prequalification.services?.map(s => typeof s === 'object' ? s.name : s).sort().join(', ') || '';
      const newServices = formData.services?.map(s => typeof s === 'object' ? s.name : s).sort().join(', ') || '';
      if (oldServices !== newServices) changedFields.push(`Services: ${newServices || 'None'}`);

      if (isTender) {
        if (formData.title !== (prequalification.title || '')) changedFields.push('Title');
        if (formData.type !== (prequalification.type || '')) changedFields.push('Type');
        if (String(formData.proposalValue || '') !== String(prequalification.proposalValue || '')) changedFields.push('Proposal Value');
        if (String(formData.grossProfit || '') !== String(prequalification.grossProfit || '')) changedFields.push('Gross Profit');
        if (String(formData.wht || '') !== String(prequalification.wht || '')) changedFields.push('WHT');
        if (String(formData.mobilizationAdvance || '') !== String(prequalification.mobilizationAdvance || '')) changedFields.push('Mobilization Advance');
        if (formData.contractDuration !== (prequalification.contractDuration || '')) changedFields.push('Contract Duration');
        if (String(formData.noOfStaff || '') !== String(prequalification.noOfStaff || '')) changedFields.push('No of Staff');
        if (String(formData.earnestMoneyValue || '') !== String(prequalification.earnestMoneyValue || '')) changedFields.push('Earnest Money Value');
        if (formData.paymentTerms !== (prequalification.paymentTerms || '')) changedFields.push('Payment Terms');
        if (formData.winningStrategy !== (prequalification.winningStrategy || '')) changedFields.push('Winning Strategy');
        if (formData.notes !== (prequalification.notes || '')) changedFields.push('Notes / Scope');
      }

      // For profiles the current status is stored in `status`, not `stage`
      const currentStatus = isProfile ? (prequalification.status || 'New') : prequalification.stage;
      if (effectiveStage !== currentStatus || changedFields.length > 0) {
        const isLoss = effectiveStage === 'Lost' || effectiveStage === 'Disqualified';
        const lossLabel = isLoss
          ? (LOSS_REASONS.find((r) => r.id === savePayload.lostReason)?.label || savePayload.lostReason)
          : null;
        
        const activityData = {
          from: isProfile ? (prequalification.status || 'New') : prequalification.stage,
          to: effectiveStage,
          note: savePayload.lostReasonNote || '',
          lossReason: lossLabel,
          editedFields: changedFields.length > 0 ? changedFields.join(', ') : null
        };
        
        const type = effectiveStage !== prequalification.stage ? 'STAGE_CHANGE' : (isTender ? 'details_updated' : 'DETAILS_UPDATED');

        if (isTender) {
          tendersService.addNote(entityId, {
            content: JSON.stringify(activityData),
            type,
            userId: currentUser?.id
          }).catch(e => console.error('Failed to log stage changes:', e));
        } else if (isProfile) {
          contactService.addNote(entityId, {
            content: JSON.stringify(activityData),
            type,
            userId: currentUser?.id
          }).catch(e => console.error('Failed to log stage changes:', e));
        } else {
          prequalificationService.addNote(entityId, {
            content: JSON.stringify(activityData),
            type,
            userId: currentUser?.id
          }).catch(e => console.error('Failed to log stage changes:', e));
        }
      }

      // Fire-and-forget side-effects AFTER the primary save succeeds
      // Skip onSaveLead for profiles — contactService.logCall already persisted all changes
      if (onSaveLead && !isProfile) {
        // Normalize services to string names — the server schema expects string[], not object[]
        const normalizedServices = (formData.services || []).map(s =>
          typeof s === 'object' && s !== null ? s.name : s
        ).filter(Boolean);

        const leadUpdatePayload = {
          stage: savePayload.stage,
          lostReason: savePayload.lostReason,
          lostReasonNote: savePayload.lostReasonNote,
          documentStatus: savePayload.documentStatus,
          submissionDate: savePayload.submissionDate,
          earnestMoneyRequired: savePayload.earnestMoneyRequired,
          earnestMoneyValue: savePayload.earnestMoneyValue,
          statusRemarks: savePayload.statusRemarks,
          attachmentUrl: savePayload.attachmentUrl,
          services: normalizedServices,
          notes: isTender ? savePayload.notes : savePayload.enlistmentRemarks,
          ...(isTender && {
            title: savePayload.title,
            type: savePayload.type,
            assignedTo: savePayload.assignedTo,
            proposalValue: savePayload.proposalValue,
            negotiation: savePayload.negotiation,
            grossProfit: savePayload.grossProfit,
            gpPercent: savePayload.gpPercent,
            wht: savePayload.wht,
            mobilizationAdvance: savePayload.mobilizationAdvance,
            noOfStaff: savePayload.noOfStaff,
            contractDuration: savePayload.contractDuration,
            paymentTerms: savePayload.paymentTerms,
            winningStrategy: savePayload.winningStrategy
          })
        };


        onSaveLead(prequalification.id, leadUpdatePayload).catch(e => console.error(e));
      }

      if (callLog.nextCallDate && shouldAutoSync('call') && onSaveLead && !isLostOutcome) {
        try {
          const updatedLead = { ...prequalification, ...savePayload };
          syncLeadEvent(updatedLead, 'call', onSaveLead).catch(e => console.error(e));
        } catch (err) {
          console.error('Calendar sync error:', err);
        }
      }

      if (contactDirty) {
        await handleSaveContact();
      }

      onClose(); // close only after successful primary save
    } catch (error) {
      console.error('Error saving call log:', error);
      setToast({ message: 'Failed to save. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Stage Tab Content (context-aware) ──────────────────────────────────────
  const renderTenderFormFields = () => (
    <>
      <div className="sm:col-span-3">
        <label className="text-xs font-semibold text-gray-600">Title</label>
        <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
          value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600 block mb-1.5">{t('services') || 'Services'}</label>
        <Dropdown>
          <DropdownTrigger>
            <button
              type="button"
              className={`w-full p-2 min-h-[38px] border border-gray-200 rounded-lg text-sm bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isLockedStatus}
            >
              <div className="flex-1 truncate mr-2 text-left">
                {formData.services?.length === 0 || !formData.services ? (
                  <span className="text-gray-500">{t('selectServices') || 'Select Services'}</span>
                ) : formData.services?.length === 1 ? (
                  <span className="text-gray-800">{formData.services[0]}</span>
                ) : (
                  <span className="text-blue-600 font-medium">
                    {formData.services.length} {t('selected') || 'Selected'}
                  </span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
            </button>
          </DropdownTrigger>
          <DropdownPopover className="w-full max-h-60 overflow-y-auto z-50">
            <DropdownMenu
              selectedKeys={new Set(formData.services || [])}
              selectionMode="multiple"
              onSelectionChange={(keys) => setFormData({ ...formData, services: Array.from(keys) })}
            >
              {(availableServices || []).map((s) => (
                <DropdownItem key={s.name} id={s.name}>{s.name}</DropdownItem>
              ))}
            </DropdownMenu>
          </DropdownPopover>
        </Dropdown>
      </div>
      
      <div>
        <label className="text-xs font-semibold text-gray-600">Type</label>
        <Dropdown className="w-full mt-1">
          <DropdownTrigger className="w-full">
            <div className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500">
              <span>{formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender'}</span>
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
            </div>
          </DropdownTrigger>
          <DropdownPopover placement="bottom">
            <DropdownMenu
              selectedKeys={new Set([formData.type || 'TENDER'])}
              onSelectionChange={(keys) => setFormData({ ...formData, type: Array.from(keys)[0] })}
              selectionMode="single"
            >
              <DropdownItem id="TENDER">Tender</DropdownItem>
              <DropdownItem id="PROPOSAL">Proposal</DropdownItem>
            </DropdownMenu>
          </DropdownPopover>
        </Dropdown>
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600">Contract End Date / Duration</label>
        <div className="mt-1">
          <DatePicker
            value={formData.contractDuration || ''}
            onChange={(e) => setFormData({ ...formData, contractDuration: e.target.value })}
            name="contractDuration"
            placeholder="Select expiry date"
          />
        </div>
        {(() => {
          if (!formData.contractDuration || !formData.contractDuration.includes('-')) return null;
          const target = new Date(formData.contractDuration);
          if (isNaN(target.getTime())) return null;
          const now = new Date();
          if (target.getTime() <= now.getTime()) return <p className="text-[10px] text-red-500 font-medium mt-1">Expired</p>;
          
          let years = target.getFullYear() - now.getFullYear();
          let months = target.getMonth() - now.getMonth();
          let days = target.getDate() - now.getDate();
          
          if (days < 0) {
            months -= 1;
            days += new Date(target.getFullYear(), target.getMonth(), 0).getDate();
          }
          if (months < 0) {
            years -= 1;
            months += 12;
          }
          
          const parts = [];
          if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
          if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
          if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
          
          return (
            <p className="text-[10px] text-blue-600 font-medium mt-1">
              {parts.length > 0 ? parts.join(', ') + ' remaining' : 'Expires today'}
            </p>
          );
        })()}
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600">No of Staff</label>
        <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
          value={formData.noOfStaff || ''} onChange={(e) => setFormData({ ...formData, noOfStaff: e.target.value })} placeholder="Required staff count" />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600">Proposal Value</label>
        <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
          value={formData.proposalValue || ''} onChange={(e) => setFormData({ ...formData, proposalValue: e.target.value })} placeholder="e.g. 500000" />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600">Negotiated Amount</label>
        <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
          value={formData.negotiation || ''} onChange={(e) => setFormData({ ...formData, negotiation: e.target.value })} placeholder="Negotiation amount" />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600">Earnest Money Value</label>
        <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
          value={formData.earnestMoneyValue || ''} onChange={(e) => setFormData({ ...formData, earnestMoneyValue: e.target.value })} placeholder="Earnest Money Amount" />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600">Gross Profit</label>
        <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
          value={formData.grossProfit || ''} onChange={(e) => setFormData({ ...formData, grossProfit: e.target.value })} placeholder="e.g. 50000" />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600">GP %</label>
        <input readOnly className="w-full p-2 border rounded-lg text-sm mt-1 bg-emerald-50 border-emerald-300 font-bold text-emerald-800 outline-none cursor-not-allowed"
          value={formData.gpPercent || ''} placeholder="Auto calculated %" />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600">WHT</label>
        <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
          value={formData.wht || ''} onChange={(e) => setFormData({ ...formData, wht: e.target.value })} placeholder="WHT Amount or %" />
      </div>
      
      <div>
        <label className="text-xs font-semibold text-gray-600">Mobilization Advance</label>
        <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
          value={formData.mobilizationAdvance || ''} onChange={(e) => setFormData({ ...formData, mobilizationAdvance: e.target.value })} placeholder="Advance amount" />
      </div>

      <div className="hidden sm:block"></div>

      <div className="sm:col-span-3">
        <label className="text-xs font-semibold text-gray-600">Payment Terms</label>
        <textarea className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 h-16 bg-white"
          value={formData.paymentTerms || ''} onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })} placeholder="e.g. Net 30, 50% Advance" />
      </div>

      <div className="sm:col-span-3">
        <label className="text-xs font-semibold text-gray-600">Notes / Scope</label>
        <textarea className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 h-24 bg-white"
          value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
      </div>
    </>
  );

  const renderStageContent = () => {
    const stage = formData.stage;
    const isDisqualified = stage === 'Disqualified';
    const isLost = stage === 'Lost';
    const isEnlisted = stage === 'Enlisted';
    const isWon = stage === 'Won';
    const isInProcess = stage === 'In Process' || stage === 'Hold';

    const renderServicesField = () => (
      <div className="sm:col-span-2 border-t border-dashed border-gray-200/60 pt-4 mt-2">
        <label className="text-xs font-semibold text-gray-600 block mb-1.5">{t('services') || 'Services'}</label>
        <Dropdown>
          <DropdownTrigger>
            <button
              type="button"
              className={`w-full p-2 min-h-[38px] border border-white/50 rounded-lg text-sm bg-white/70 text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isLockedStatus}
            >
              <div className="flex-1 truncate mr-2 text-left">
                {formData.services?.length === 0 || !formData.services ? (
                  <span className="text-gray-500">{t('selectServices') || 'Select Services'}</span>
                ) : formData.services?.length === 1 ? (
                  <span className="text-gray-800">{formData.services[0]}</span>
                ) : (
                  <span className="text-blue-600 font-medium">
                    {formData.services.length} {t('selected') || 'Selected'}
                  </span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
            </button>
          </DropdownTrigger>
          <DropdownPopover className="w-full max-h-60 overflow-y-auto z-50">
            <DropdownMenu
              selectedKeys={new Set(formData.services || [])}
              selectionMode="multiple"
              onSelectionChange={(keys) => setFormData({ ...formData, services: Array.from(keys) })}
            >
              {(availableServices || []).map((s) => (
                <DropdownItem key={s.name} id={s.name}>{s.name}</DropdownItem>
              ))}
            </DropdownMenu>
          </DropdownPopover>
        </Dropdown>
      </div>
    );

    if (isTender) {
      return (
        <div className="max-w-2xl mx-auto space-y-6">


          {/* Previous Tenders History */}
          {prequalification.profile?.tenders && prequalification.profile.tenders.filter(t => String(t.id) !== String(prequalification.id) && new Date(t.createdAt) < new Date(prequalification.createdAt)).length > 0 && (
            <div className="bg-purple-50/30 p-4 rounded-xl border border-purple-200 shadow-sm">
              <h3 className="text-xs font-bold text-purple-700 uppercase tracking-widest flex items-center gap-2 mb-3">
                <Clock className="w-3.5 h-3.5" /> Previous Tenders / Proposals
              </h3>
              <div className="space-y-4">
                {prequalification.profile.tenders.filter(t => String(t.id) !== String(prequalification.id) && new Date(t.createdAt) < new Date(prequalification.createdAt)).map(prevTender => (
                  <div key={prevTender.id} className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-3 border-b border-purple-100 last:border-0 pb-3 last:pb-0">
                    <div className="col-span-1 sm:col-span-3">
                      <label className="block text-xs font-bold text-gray-400 uppercase">Tender / Proposal Title</label>
                      <p className="text-sm font-bold text-purple-900 mt-0.5">
                        {prevTender.title || 'Untitled'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase">Created On</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {prevTender.createdAt ? new Date(prevTender.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase">Submission Date</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {prevTender.submissionDate ? new Date(prevTender.submissionDate).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase">Type</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {prevTender.type === 'PROPOSAL' ? 'Proposal' : 'Tender'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase">Source</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {prevTender.source?.name || '—'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase">Assignee</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {prevTender.assignee?.username || prevTender.assignee?.name || '—'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase">Status</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {prevTender.status || '—'}
                      </p>
                    </div>
                    <div className="sm:col-span-3">
                      <label className="block text-xs font-bold text-gray-400 uppercase">Services</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {prevTender.services?.length ? prevTender.services.map(s => typeof s === 'string' ? s : s.name).join(', ') : '—'}
                      </p>
                    </div>
                    {prevTender.status === 'Lost' && (
                      <div className="sm:col-span-3 bg-red-50/50 p-2.5 rounded-lg border border-red-100">
                        <label className="block text-[10px] font-bold text-red-500 uppercase tracking-wide">Lost Reason</label>
                        <p className="text-sm font-semibold text-red-700 mt-0.5">
                          {prevTender.lostReason || 'Not specified'} 
                          {prevTender.lostReasonNote ? <span className="font-normal text-red-600 ml-1">- {prevTender.lostReasonNote}</span> : ''}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {prequalification.prequalification && (
            <div className="bg-teal-50/30 p-4 rounded-xl border border-teal-200 shadow-sm">
              <h3 className="text-xs font-bold text-teal-700 uppercase tracking-widest flex items-center gap-2 mb-3">
                <Clock className="w-3.5 h-3.5" /> Prequalification History
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-3">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase">Created On</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {prequalification.prequalification.createdAt ? new Date(prequalification.prequalification.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase">Submission Date</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {prequalification.prequalification.submissionDate ? new Date(prequalification.prequalification.submissionDate).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase">Enlisted On</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {prequalification.prequalification.enlistedDate 
                      ? new Date(prequalification.prequalification.enlistedDate).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) 
                      : (prequalification.prequalification.updatedAt ? new Date(prequalification.prequalification.updatedAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—')}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase">Assignee</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {prequalification.prequalification.assignee?.username || prequalification.prequalification.assignee?.name || '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase">Source</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {prequalification.prequalification.source?.name || (typeof prequalification.prequalification.source === 'string' ? prequalification.prequalification.source : '') || '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase">Document Status</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {prequalification.prequalification.documentStatus || '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase">Earnest Money</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {prequalification.prequalification.earnestMoneyRequired 
                      ? (prequalification.prequalification.earnestMoneyValue ? `Required (${prequalification.prequalification.earnestMoneyValue})` : 'Required') 
                      : 'Not Required'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase">Attachment URL</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5 truncate">
                    {prequalification.prequalification.attachmentUrl ? (
                      <a href={getFileUrl(prequalification.prequalification.attachmentUrl)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        View Document
                      </a>
                    ) : '—'}
                  </p>
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-xs font-bold text-gray-400 uppercase">Services</label>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {prequalification.prequalification.services?.length ? prequalification.prequalification.services.map(s => typeof s === 'string' ? s : s.name).join(', ') : '—'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50/30 p-6 rounded-xl border border-blue-200 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-blue-200 pb-3">
              <Edit3 className="w-4 h-4 shrink-0" /> Tender / Proposal Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {renderTenderFormFields()}
            </div>
            {isWon && (
              <div className="mt-4 p-3 bg-emerald-100/60 rounded-xl border border-emerald-200 flex items-center gap-2 text-xs text-emerald-700 font-medium">
                <CheckCircle className="w-4 h-4 shrink-0" />
                This tender/proposal is marked as <strong>Won</strong>. Save to confirm and create the contract.
              </div>
            )}
            {(isLost || isDisqualified) && (
              <div className="mt-4 border-t border-blue-200 pt-4 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600">{isDisqualified ? 'Disqualification Reason' : 'Loss Reason'}</label>
                  <Dropdown className="w-full mt-1">
                    <DropdownTrigger className="w-full">
                      <div className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500">
                        <span>{LOSS_REASONS.find(r => r.id === lossReason)?.label || 'Select Reason'}</span>
                        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                      </div>
                    </DropdownTrigger>
                    <DropdownPopover placement="bottom" className="w-full max-h-60 overflow-y-auto">
                      <DropdownMenu
                        selectedKeys={new Set([lossReason])}
                        onSelectionChange={(keys) => setLossReason(Array.from(keys)[0])}
                        selectionMode="single"
                      >
                        {LOSS_REASONS.filter(r => isDisqualified ? r.klass === 'disqualified' : true).map(r => (
                          <DropdownItem key={r.id} id={r.id}>{r.label}</DropdownItem>
                        ))}
                      </DropdownMenu>
                    </DropdownPopover>
                  </Dropdown>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Notes (Optional)</label>
                  <textarea className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 h-16"
                    value={stageChangeNote} onChange={(e) => setStageChangeNote(e.target.value)} />
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (isWon) {
      return (
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="bg-emerald-50/40 p-6 rounded-xl border border-emerald-200 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-emerald-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-emerald-200 pb-3">
              <Trophy className="w-4 h-4 shrink-0" /> Won — Booking Confirmation
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600">{t('documentStatus') || 'Document Status'}</label>
                <Dropdown>
                  <DropdownTrigger>
                    <button type="button" className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white text-left flex justify-between items-center mt-1 min-h-[38px]">
                      {enlistmentData.documentStatus}
                      <ChevronDown className="w-4 h-4 text-gray-400 ml-2" />
                    </button>
                  </DropdownTrigger>
                  <DropdownPopover className="w-full">
                    <DropdownMenu selectedKeys={new Set([enlistmentData.documentStatus])} selectionMode="single"
                      onSelectionChange={(keys) => setEnlistmentData({ ...enlistmentData, documentStatus: Array.from(keys)[0] })}>
                      {(DOCUMENT_STATUSES || ['To be Received', 'In Process', 'Submitted']).map(s => (
                        <DropdownItem key={s} id={s}>{s}</DropdownItem>
                      ))}
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Status Remarks</label>
                <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={enlistmentData.statusRemarks}
                  onChange={e => setEnlistmentData({ ...enlistmentData, statusRemarks: e.target.value })}
                  placeholder="Remarks..." />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Attachment / File Link</label>
                <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={enlistmentData.attachmentUrl}
                  onChange={e => setEnlistmentData({ ...enlistmentData, attachmentUrl: e.target.value })}
                  placeholder="Attachment URL" />
              </div>
              {renderServicesField()}
            </div>
            <div className="mt-2 p-3 bg-emerald-100/60 rounded-xl border border-emerald-200 flex items-center gap-2 text-xs text-emerald-700 font-medium">
              <CheckCircle className="w-4 h-4 shrink-0" />
              This lead is marked as <strong>Won</strong>. Please open the full lead to fill booking details.
            </div>
          </div>
        </div>
      );
    }

    if (isLost || isDisqualified) {
      const disqualifyingIds = LOSS_REASONS.filter(r => r.klass === 'disqualified').map(r => r.id);
      const filteredReasons = isDisqualified
        ? LOSS_REASONS.filter(r => r.klass === 'disqualified')
        : LOSS_REASONS;

      return (
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="bg-red-50/30 p-6 rounded-xl border border-red-200 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-red-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-red-200 pb-3">
              <XCircle className="w-4 h-4 shrink-0" />
              {isDisqualified ? 'Disqualification Reason' : 'Loss Reason'} <span className="text-red-400 normal-case">*</span>
            </h3>
            <div className="max-h-64 overflow-y-auto rounded-xl border border-red-100 divide-y divide-red-50">
              {filteredReasons.map(r => (
                <button key={r.id} type="button" onClick={() => setLossReason(r.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2.5 ${lossReason === r.id ? 'bg-red-50 text-red-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${lossReason === r.id ? 'bg-red-500' : 'bg-gray-200'}`} />
                  {t('loss_' + r.id) || r.label}
                </button>
              ))}
            </div>
            {lossReason === 'postponed' && (
              <p className="text-[11px] text-amber-600 leading-snug">
                Postponed events are warm leads — set a follow-up date before saving.
              </p>
            )}
          </div>
          <div className="bg-red-50/20 p-6 rounded-xl border border-red-100 shadow-sm space-y-3">
            <h3 className="text-xs font-bold text-red-600 uppercase tracking-widest flex items-center gap-1.5 border-b border-red-100 pb-3">
              <Edit3 className="w-4 h-4 shrink-0" />
              {lossReason === 'other' ? 'Notes (Required)' : 'Notes (Optional)'}
            </h3>
            <textarea
              className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-100 focus:border-red-400 h-24 resize-none bg-white"
              value={stageChangeNote}
              onChange={e => setStageChangeNote(e.target.value)}
              placeholder={lossReason === 'competitor' ? 'Which competitor did they choose?' : 'Add any notes about this outcome...'}
            />
          </div>
        </div>
      );
    }

    if (isEnlisted) {
      return (
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="bg-green-50/30 p-6 rounded-xl border border-green-200 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-green-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-green-200 pb-3">
              <CheckCircle className="w-4 h-4 shrink-0" /> Enlistment Details
            </h3>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Enlistment Remarks</label>
              <textarea name="remarks" rows={3}
                value={enlistmentData.remarks}
                onChange={e => setEnlistmentData({ ...enlistmentData, remarks: e.target.value })}
                placeholder="Any notes about this enlistment..."
                className="w-full p-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none bg-white" />
            </div>
          </div>

          <div className="bg-amber-50/30 p-6 rounded-xl border border-amber-200 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-amber-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-amber-200 pb-3">
              <ClipboardList className="w-4 h-4 shrink-0" /> Document & Submission
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600">Document Status</label>
                <Dropdown>
                  <DropdownTrigger>
                    <button type="button" className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white text-left flex justify-between items-center mt-1 min-h-[38px]">
                      {enlistmentData.documentStatus}
                      <ChevronDown className="w-4 h-4 text-gray-400 ml-2" />
                    </button>
                  </DropdownTrigger>
                  <DropdownPopover className="w-full">
                    <DropdownMenu selectedKeys={new Set([enlistmentData.documentStatus])} selectionMode="single"
                      onSelectionChange={(keys) => setEnlistmentData({ ...enlistmentData, documentStatus: Array.from(keys)[0] })}>
                      {(DOCUMENT_STATUSES || ['To be Received', 'In Process', 'Submitted']).map(s => (
                        <DropdownItem key={s} id={s}>{s}</DropdownItem>
                      ))}
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Submission Date</label>
                <DatePicker value={enlistmentData.submissionDate}
                  onChange={e => setEnlistmentData({ ...enlistmentData, submissionDate: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Earnest Money</label>
                <div className="flex items-center gap-4 border border-gray-200 rounded-lg p-2.5 bg-white">
                  {[{ val: false, label: 'Not Required' }, { val: true, label: 'Required' }].map(opt => (
                    <label key={String(opt.val)} className="flex items-center gap-1.5 cursor-pointer text-xs font-medium">
                      <input type="radio" name="emRequired"
                        checked={enlistmentData.earnestMoneyRequired === opt.val}
                        onChange={() => setEnlistmentData({ ...enlistmentData, earnestMoneyRequired: opt.val, earnestMoneyValue: opt.val ? enlistmentData.earnestMoneyValue : '' })} />
                      <span className={opt.val ? 'text-blue-700' : ''}>{opt.label}</span>
                    </label>
                  ))}
                  {enlistmentData.earnestMoneyRequired && (
                    <input type="number" onWheel={e => e.target.blur()}
                      className="flex-1 p-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white"
                      value={enlistmentData.earnestMoneyValue}
                      onChange={e => setEnlistmentData({ ...enlistmentData, earnestMoneyValue: e.target.value })}
                      placeholder="PKR Amount" />
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Status Remarks</label>
                <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={enlistmentData.statusRemarks}
                  onChange={e => setEnlistmentData({ ...enlistmentData, statusRemarks: e.target.value })}
                  placeholder="Remarks..." />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Attachment / File Link</label>
                <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={enlistmentData.attachmentUrl}
                  onChange={e => setEnlistmentData({ ...enlistmentData, attachmentUrl: e.target.value })}
                  placeholder="Attachment URL" />
              </div>
              {renderServicesField()}
            </div>
          </div>
        </div>
      );
    }

    if (isInProcess) {
      return (
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="bg-purple-50/30 p-6 rounded-xl border border-purple-200 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-purple-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-purple-200 pb-3">
              <Briefcase className="w-4 h-4 shrink-0" /> Tender / Proposal — In Process
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600">Document Status</label>
                <Dropdown>
                  <DropdownTrigger>
                    <button type="button" className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white text-left flex justify-between items-center mt-1 min-h-[38px]">
                      {enlistmentData.documentStatus}
                      <ChevronDown className="w-4 h-4 text-gray-400 ml-2" />
                    </button>
                  </DropdownTrigger>
                  <DropdownPopover className="w-full">
                    <DropdownMenu selectedKeys={new Set([enlistmentData.documentStatus])} selectionMode="single"
                      onSelectionChange={(keys) => setEnlistmentData({ ...enlistmentData, documentStatus: Array.from(keys)[0] })}>
                      {(DOCUMENT_STATUSES || ['To be Received', 'In Process', 'Submitted']).map(s => (
                        <DropdownItem key={s} id={s}>{s}</DropdownItem>
                      ))}
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Submission Date</label>
                <DatePicker value={enlistmentData.submissionDate}
                  onChange={e => setEnlistmentData({ ...enlistmentData, submissionDate: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Status / Remarks</label>
                <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px] outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-400"
                  value={enlistmentData.statusRemarks}
                  onChange={e => setEnlistmentData({ ...enlistmentData, statusRemarks: e.target.value })}
                  placeholder="Current status or remarks..." />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Attachment / File Link</label>
                <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px] outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-400"
                  value={enlistmentData.attachmentUrl}
                  onChange={e => setEnlistmentData({ ...enlistmentData, attachmentUrl: e.target.value })}
                  placeholder="Attachment URL" />
              </div>
              {renderServicesField()}
            </div>
          </div>
        </div>
      );
    }

    // Default: New / Submitted / Pending
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-blue-50/30 p-6 rounded-xl border border-blue-200 shadow-sm space-y-4">
          <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-blue-200 pb-3">
            <ClipboardList className="w-4 h-4 shrink-0" /> {formData.stage} — Prequalification Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600">Document Status</label>
              <Dropdown>
                <DropdownTrigger>
                  <button type="button" className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white text-left flex justify-between items-center mt-1 min-h-[38px]">
                    {enlistmentData.documentStatus}
                    <ChevronDown className="w-4 h-4 text-gray-400 ml-2" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-full">
                  <DropdownMenu selectedKeys={new Set([enlistmentData.documentStatus])} selectionMode="single"
                    onSelectionChange={(keys) => setEnlistmentData({ ...enlistmentData, documentStatus: Array.from(keys)[0] })}>
                    {(DOCUMENT_STATUSES || ['To be Received', 'In Process', 'Submitted']).map(s => (
                      <DropdownItem key={s} id={s}>{s}</DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Submission Date</label>
              <DatePicker value={enlistmentData.submissionDate}
                onChange={e => setEnlistmentData({ ...enlistmentData, submissionDate: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Status Remarks</label>
              <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={enlistmentData.statusRemarks}
                onChange={e => setEnlistmentData({ ...enlistmentData, statusRemarks: e.target.value })}
                placeholder="Remarks..." />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Attachment / File Link</label>
              <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={enlistmentData.attachmentUrl}
                onChange={e => setEnlistmentData({ ...enlistmentData, attachmentUrl: e.target.value })}
                placeholder="Attachment URL" />
            </div>
            {renderServicesField()}
          </div>
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="bg-white w-full sm:max-w-5xl h-[95vh] sm:h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden relative"
      >
        {/* ── Header ── */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-start gap-3 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                {prequalification?.profile?.projectName 
                  ? `${prequalification.profile.projectName} - ${prequalification.clientName}`
                  : prequalification.clientName}
              </h2>
              <span className={`px-2.5 py-0.5 text-[10px] sm:text-xs rounded-full border font-bold uppercase tracking-wider whitespace-nowrap ${getStageBadgeClass(formData.stage)}`}>
                {formData.stage}
              </span>
              {isTender && (
                <span className="px-2 py-0.5 text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded-full font-bold whitespace-nowrap flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> Tender
                </span>
              )}
              {isLockedStatus && (
                <span className="flex items-center gap-1 px-2.5 py-0.5 bg-red-50 text-red-600 border border-red-200 rounded-full text-[10px] font-bold whitespace-nowrap">
                  <Lock className="w-3 h-3 shrink-0" /> {t('locked') || 'Locked'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
              {prequalification.phone && (
                <span className="flex items-center gap-1 font-mono font-bold text-gray-700">
                  <Phone className="w-3 h-3" />{prequalification.phone}
                </span>
              )}
              {prequalification.createdAt && (
                <span className="flex items-center gap-1 font-medium whitespace-nowrap">
                  <Clock className="w-3 h-3" />
                  <span className="text-gray-400 font-normal">{t('created') || 'Created'}</span>
                  {new Date(prequalification.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors shrink-0">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── Stage / Status Pills ── */}
        <div className="bg-slate-50 border-b border-gray-200 px-3 sm:px-6 py-2 flex gap-1.5 overflow-x-auto no-scrollbar shrink-0 items-center">

          {stages.map(s => {
            const isActive = formData.stage === s;
            const isLost = s === 'Lost';
            const activeClass = isProfile
              ? (isLost ? 'bg-red-500 text-white border-red-500 shadow-sm scale-105' : 'bg-emerald-600 text-white border-emerald-600 shadow-sm scale-105')
              : 'bg-blue-600 text-white border-blue-600 shadow-sm scale-105';
            const hoverClass = isProfile
              ? (isLost ? 'hover:border-red-400 hover:text-red-600' : 'hover:border-emerald-400 hover:text-emerald-600')
              : 'hover:border-blue-400 hover:text-blue-600';
            return (
              <button
                key={s}
                onClick={() => handleStageClick(s)}
                disabled={isLockedStatus}
                className={`px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-bold transition-all border whitespace-nowrap ${
                  isLockedStatus
                    ? 'opacity-40 cursor-not-allowed bg-white text-gray-400 border-gray-200'
                    : isActive
                      ? activeClass
                      : `bg-white text-gray-500 border-gray-200 ${hoverClass}`
                }`}
              >
                {t(s) || s}
              </button>
            );
          })}
        </div>

        {/* ── Convert Profile Prompt ── */}
        <AnimatePresence>
          {showConvertPrompt && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-emerald-50 border-b border-emerald-200 px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 overflow-hidden"
            >
              <div className="flex items-center gap-2 text-emerald-800 text-sm">
                <Info className="w-5 h-5 text-emerald-600 shrink-0" />
                <span>
                  This profile is marked as <strong>Interested</strong>. Convert it to a Prequalification, Tender, or Proposal?
                </span>
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                <button
                  onClick={async () => {
                    const idToUpdate = prequalification.originalId || prequalification.id;
                    try {
                      await contactService.update(idToUpdate, { status: 'Interested' });
                    } catch (e) {
                      console.error(e);
                    }
                    onClose();
                    const cleanProfile = { ...prequalification, id: idToUpdate, status: 'Interested' };
                    onConvertProfile?.(cleanProfile, 'PREQUALIFICATION');
                  }}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors flex items-center gap-1.5"
                >
                  <ClipboardList className="w-4 h-4" />
                  Create Prequalification
                </button>
                <button
                  onClick={async () => {
                    const idToUpdate = prequalification.originalId || prequalification.id;
                    try {
                      await contactService.update(idToUpdate, { status: 'Interested' });
                    } catch (e) {
                      console.error(e);
                    }
                    onClose();
                    const cleanProfile = { ...prequalification, id: idToUpdate, status: 'Interested' };
                    onConvertProfile?.(cleanProfile, 'TENDER');
                  }}
                  className="px-3 py-1.5 bg-white hover:bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-lg border border-emerald-200 transition-colors flex items-center gap-1.5"
                >
                  <Briefcase className="w-4 h-4" />
                  Create Tender
                </button>
                <button
                  onClick={async () => {
                    const idToUpdate = prequalification.originalId || prequalification.id;
                    try {
                      await contactService.update(idToUpdate, { status: 'Interested' });
                    } catch (e) {
                      console.error(e);
                    }
                    onClose();
                    const cleanProfile = { ...prequalification, id: idToUpdate, status: 'Interested' };
                    onConvertProfile?.(cleanProfile, 'PROPOSAL');
                  }}
                  className="px-3 py-1.5 bg-white hover:bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-lg border border-emerald-200 transition-colors flex items-center gap-1.5"
                >
                  <FileText className="w-4 h-4" />
                  Create Proposal
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Content Area ── */}
        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden bg-slate-50">
          {/* Sidebar */}
          <div className="w-full sm:w-48 bg-white border-b sm:border-b-0 sm:border-r border-gray-200 p-2 sm:p-3 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-x-visible no-scrollbar shrink-0">
            {TABS.filter(tab => isProfile ? tab.id !== 'stage' : true).map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${isActive ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'}`}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="hidden sm:inline">{language === 'ur' ? tab.labelUr : tab.label}</span>
                  {tab.id === 'timeline' && timeline.length > 0 && (
                    <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{timeline.length}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Main scroll area */}
          <div className="flex-1 overflow-y-auto p-4 md:p-8">

            {/* ── INTERACTION TAB ── */}
            {activeTab === 'interaction' && (
              <div className="max-w-2xl mx-auto space-y-6">
                
                {/* Interaction Type Selector */}
                <div className="bg-white p-2 rounded-xl border border-gray-200 shadow-sm flex flex-wrap gap-1">
                  {Object.keys(INTERACTION_CONFIG).map(type => (
                    <button
                      key={type}
                      onClick={() => { setInteractionType(type); setCallLog({ ...callLog, outcome: '' }); }}
                      className={`flex-1 min-w-[100px] py-2 px-3 text-xs sm:text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        interactionType === type
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {type === 'Call' && <Phone className="w-4 h-4" />}
                      {type === 'Meeting' && <Users className="w-4 h-4" />}
                      {type === 'Visit' && <MapPin className="w-4 h-4" />}
                      {type === 'Email' && <Mail className="w-4 h-4" />}
                      {type === 'Document' && <FileText className="w-4 h-4" />}
                      {type}
                    </button>
                  ))}
                </div>

                {/* Quick Actions (Only for Call/Email) */}
                {(interactionType === 'Call' || interactionType === 'Email') && (
                  <div className="bg-blue-50/30 p-6 rounded-xl border border-blue-200 shadow-sm space-y-4">
                    <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-blue-200 pb-3">
                      {interactionType === 'Call' ? <Phone className="w-4 h-4 shrink-0" /> : <Mail className="w-4 h-4 shrink-0" />}
                      {language === 'ur' ? (interactionType === 'Call' ? 'پہلے کال کریں' : 'ای میل کریں') : `Make the ${interactionType} First`}
                    </h3>
                    <div className="flex gap-3">
                      {interactionType === 'Call' && (
                        <button type="button" onClick={() => {
                            navigator.clipboard.writeText(prequalification.phone || contactDraft?.mobile);
                            setToast({ message: language === 'ur' ? 'فون نمبر کاپی ہو گیا' : 'Phone number copied to clipboard', type: 'success' });
                          }}
                          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm shadow-blue-200">
                          <Phone className="w-4 h-4" />
                          {language === 'ur' ? 'فون نمبر کاپی کریں' : 'Copy Phone Number'}
                        </button>
                      )}
                      {interactionType === 'Email' && (
                        <button type="button" onClick={() => {
                            navigator.clipboard.writeText(prequalification.email || contactDraft?.email);
                            setToast({ message: language === 'ur' ? 'ای میل کاپی ہو گئی' : 'Email copied to clipboard', type: 'success' });
                          }}
                          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm shadow-blue-200">
                          <Mail className="w-4 h-4" />
                          Copy Email Address
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Interaction Outcome */}
                <div className="bg-amber-50/30 p-6 rounded-xl border border-amber-200 shadow-sm space-y-4">
                  <h3 className="text-xs font-bold text-amber-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-amber-200 pb-3">
                    <MessageCircle className="w-4 h-4 shrink-0" />
                    {interactionType} Outcome {formData.stage !== 'Enlisted' && <span className="text-red-400 normal-case">*</span>}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {INTERACTION_CONFIG[interactionType].map(outcome => {
                      const Icon = outcome.icon;
                      const isSelected = callLog.outcome === outcome.id;
                      const styles = OUTCOME_STYLES[outcome.color];
                      return (
                        <button key={outcome.id} onClick={() => handleOutcomeSelect(outcome.id)}
                          className={`p-2.5 rounded-xl border-2 text-left text-xs font-semibold flex items-center gap-2.5 transition-all ${isSelected ? styles.btn : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`}>
                          <Icon className={`w-4 h-4 shrink-0 ${isSelected ? styles.icon : 'text-gray-400'}`} />
                          <span className="truncate leading-tight">{language === 'ur' && outcome.labelUr ? outcome.labelUr : outcome.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {(callLog.outcome === 'wrong_number' || callLog.outcome === 'connected_not_interested') && (
                    <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg p-2 flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 shrink-0" />
                      Stage will be automatically set to <strong>Lost</strong>. Go to the <strong>Stage</strong> tab to add the reason.
                    </p>
                  )}
                </div>

                {/* Notes */}
                <div className="bg-emerald-50/30 p-6 rounded-xl border border-emerald-200 shadow-sm space-y-4">
                  <h3 className="text-xs font-bold text-emerald-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-emerald-200 pb-3">
                    <Edit3 className="w-4 h-4 shrink-0" /> {t('notes') || 'Notes'}
                  </h3>
                  <textarea
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 h-24 resize-none bg-white"
                    placeholder={`Details about this ${interactionType.toLowerCase()}...`}
                    value={callLog.notes}
                    onChange={e => setCallLog({ ...callLog, notes: e.target.value })}
                  />
                </div>

                {/* Schedule Next Action */}
                <div className="bg-indigo-50/30 p-6 rounded-xl border border-indigo-200 shadow-sm space-y-4">
                  <div className="flex items-center justify-between border-b border-indigo-200 pb-3">
                    <h3 className="text-xs font-bold text-indigo-700 uppercase tracking-widest flex items-center gap-1.5">
                      <CalendarClock className="w-4 h-4 shrink-0" />
                      Schedule Next Action
                    </h3>
                  </div>
                  
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-2">Next Action Type</label>
                    <div className="flex flex-wrap gap-2">
                      {['Call', 'Meeting', 'Visit', 'Email', 'Submit Proposal'].map(type => (
                        <button
                          key={type}
                          onClick={() => setCallLog({ ...callLog, nextActionType: type })}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                            callLog.nextActionType === type
                              ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div>
                      <label className="text-xs font-semibold text-gray-600">{t('date') || 'Date'}</label>
                      <DatePicker value={callLog.nextCallDate}
                        onChange={e => setCallLog({ ...callLog, nextCallDate: e.target.value })}
                        min={todayStr} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">{t('time') || 'Time'}</label>
                      <TimePicker
                        className="mt-1"
                        value={callLog.nextCallTime}
                        onChange={e => setCallLog({ ...callLog, nextCallTime: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── PROFILE TAB ── */}
            {activeTab === 'profile' && (
              <div className="max-w-2xl mx-auto space-y-6">
                <div className="bg-indigo-50/30 p-6 rounded-xl border border-indigo-200 shadow-sm space-y-4">
                  <div className="flex items-center justify-between border-b border-indigo-200 pb-3">
                    <h3 className="text-xs font-bold text-indigo-700 uppercase tracking-widest flex items-center gap-1.5">
                      <Users className="w-4 h-4 shrink-0" /> {t('profileHeading') || 'Profile'}
                    </h3>
                  </div>

                  {!contactDraft ? (
                    <p className="text-sm text-gray-400 text-center py-4">No profile linked to this lead.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { key: 'clientName', label: t('clientName') || 'Client Name', placeholder: 'Enter client name' },
                        { key: 'contactPersonName', label: t('contactPersonName') || 'Contact Person', placeholder: 'Enter contact person' },
                        { key: 'designation', label: t('designation') || 'Designation', placeholder: 'Enter designation' },
                        { key: 'contactDetailExt', label: t('contactDetailExt') || 'Extension', placeholder: 'Enter extension' },
                        { key: 'email', label: t('email') || 'Email', placeholder: 'Enter email', type: 'email' },
                        { key: 'mobile', label: t('mobile') || 'Mobile', type: 'mobile' },
                        { key: 'city', label: t('city') || 'City', placeholder: 'City' },
                        { key: 'address', label: t('address') || 'Address', placeholder: 'Enter address' },
                        { key: 'projectName', label: t('projectName') || 'Project Name', placeholder: 'Project name' },
                      ].map(field => (
                        <div key={field.key} className={field.key === 'address' || field.key === 'projectName' ? 'sm:col-span-2' : ''}>
                          <label className="block text-xs font-bold text-gray-500 uppercase">{field.label}</label>
                          {field.key === 'city' ? (
                            <SearchableCitySelect
                              value={contactDraft.city}
                              onChange={(val) => { setContactDraft({ ...contactDraft, city: val }); setContactSaved(false); }}
                              cities={cities}
                              onCreateCity={handleCreateNewCity}
                              loading={loadingCities}
                              onToggleOpen={setIsCityDropdownOpen}
                            />
                          ) : field.type === 'mobile' ? (
                            <PhoneInput
                              country={'pk'}
                              value={contactDraft.mobile || ''}
                              onChange={(phone) => { setContactDraft({ ...contactDraft, mobile: phone }); setContactSaved(false); }}
                              containerClass="mt-1"
                              inputClass="!w-full !p-2 !pl-[48px] !text-sm !min-h-[38px] !border !border-gray-200 !rounded-lg !outline-none !transition-all focus:!ring-2 focus:!ring-blue-100 focus:!border-blue-500 bg-white"
                              buttonClass="!border-gray-200 !bg-transparent !rounded-l-lg"
                              dropdownClass="!rounded-lg !shadow-2xl !border-gray-200 !max-h-[140px] !overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
                              inputProps={{ placeholder: t('enterMobile') || 'Enter Mobile' }}
                            />
                          ) : (
                            <input
                              type={field.type || 'text'}
                              className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
                              placeholder={field.placeholder}
                              value={contactDraft[field.key] || ''}
                              onChange={e => { setContactDraft({ ...contactDraft, [field.key]: e.target.value }); setContactSaved(false); }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {contactError && <p className="text-xs text-red-600 mt-2">{contactError}</p>}
                </div>
              </div>
            )}

            {/* ── STAGE TAB ── */}
            {activeTab === 'stage' && renderStageContent()}

            {/* ── TIMELINE TAB ── */}
            {activeTab === 'timeline' && (
              <div className="flex flex-col max-w-2xl mx-auto" style={{ height: 'calc(100% - 1px)' }}>
                <div className="flex-1 min-h-0 space-y-4 mb-4 overflow-y-auto">
                  {timelineLoading ? (
                    [1, 2, 3].map(i => (
                      <div key={i} className="flex gap-3 animate-pulse">
                        <div className="w-8 h-8 rounded-full bg-gray-100 shrink-0" />
                        <div className="flex-1 bg-white p-3 rounded-xl border">
                          <div className="h-3 bg-gray-100 rounded w-3/4 mb-2" />
                          <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                        </div>
                      </div>
                    ))
                  ) : timeline.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="font-medium">{t('noActivityYet') || 'No activity yet'}</p>
                      <p className="text-xs mt-1 text-gray-300">{t('addNoteToTrack') || 'Add a note below to start tracking'}</p>
                    </div>
                  ) : (
                    timeline.map((item, i) => {
                      const meta = getActivityMeta(item.type);
                      return (
                        <div key={i} className="flex gap-3">
                          <div className={`w-8 h-8 rounded-full bg-${meta.color}-100 flex items-center justify-center flex-shrink-0`}>
                            <span className="text-sm">{meta.icon}</span>
                          </div>
                          <div className="flex-1 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                {formatActivityMessage({ type: item.type, ...item.data })}
                              </p>
                              {item.isProfileHistory && (
                                <span className="inline-block bg-indigo-100 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 shrink-0">
                                  {t('profilePhase') || 'Profile Phase'}
                                </span>
                              )}
                              {item.isPrequalHistory && (
                                <span className="inline-block bg-blue-100 text-blue-700 text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 shrink-0">
                                  {t('prequalificationPhase') || 'Prequalification Phase'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              {item.user} · {item.timestamp.toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={activityEndRef} />
                </div>

                {/* Add Note Input */}
                <div className="flex gap-2 pt-4 border-t border-gray-200 shrink-0">
                  <input
                    className="flex-1 p-3 rounded-xl border border-gray-200 outline-none text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white"
                    placeholder={t('addNotePlaceholder') || 'Add a note...'}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                    disabled={commentSending}
                  />
                  <button onClick={handleAddComment} disabled={!comment.trim() || commentSending}
                    className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {commentSending ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ── Footer ── */}
        <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-3 flex justify-between items-center shrink-0">
          <div className="text-xs text-gray-400">
            {callLog.outcome
              ? <span className="text-blue-600 font-medium flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" />
                  {INTERACTION_CONFIG[interactionType].find(o => o.id === callLog.outcome)?.label}
                </span>
              : (formData.stage === 'Enlisted'
                  ? <span className="text-blue-600 font-medium">Ready to save details</span>
                  : <span className="text-amber-500">Select a call outcome to save</span>)}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
              {t('cancel') || 'Cancel'}
            </button>
            <button
              id="log-call-save-btn"
              onClick={handleSaveCall}
              disabled={(!callLog.outcome && formData.stage !== 'Enlisted' && !isTender) || saving}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm shadow-blue-200"
            >
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? (t('saving') || 'Saving...') : (t('saveAndNext') || 'Save & Next')}
            </button>
          </div>
        </div>

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        
        {/* Render Won Confirm Modal if needed */}
        <TenderWonConfirmModal
          isOpen={showWonConfirm}
          onConfirm={() => {
            setShowWonConfirm(false);
            handleSaveCall(true); // force confirm
          }}
          onCancel={() => setShowWonConfirm(false)}
          isSaving={saving}
          canConfirm={
            !!formData.title &&
            !!formData.type &&
            !!formData.proposalValue &&
            !!formData.contractDuration &&
            !!formData.paymentTerms
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {renderTenderFormFields()}
          </div>
        </TenderWonConfirmModal>

      </motion.div>
    </motion.div>
  );
}
